import { Injectable, Injector, inject, WritableSignal } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { first } from 'rxjs/operators';
import { EvCalculationService } from '../core/ev-calculation.service';
import { WaypointService } from '../core/waypoint.service';
import { MapService } from '../map/map.service';
import { MapRangeDrawerService } from '../map/map-range-drawer.service';
import { MapRouteDrawerService } from '../map/map-route-drawer.service';
import { NavigationMotionControllerService } from '../map/navigation-motion-controller.service';
import { DataParserService } from '../core/data-parser.service';
import { WebviewService } from '../platform/webview.service';
import { SettingsService } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { NavigationService } from '../core/navigation.service';
import { CalculationError, CalculationState } from '../../models/ev-calculation-error-mapper';
import { ConfirmationDialogComponent } from '../../components/modals/confirmation-dialog/confirmation-dialog.component';
import { SubscriptionRequiredDialogComponent } from '../../components/modals/subscriptions-dialog/subscription-required-dialog/subscription-required-dialog.component';
import { MapComponent } from '../../components/map/map.component';
import { VehiclePanelComponent } from '../../components/ui-panels/vehicle-panel/vehicle-panel.component';
import { RangeParams } from '../../models/range-params';
import { LatLng } from '../../models/latlng';
import { Route, WaypointAddress } from '../../models/route';
import { Range } from '../../models/range';
import { DisplayMode } from '../../models/display-mode';
import { ChargeState } from '../../models/charge-state';
import { BetterRouteState } from '../../models/better-route';
import { WindElement } from '../../models/wind';
import { VehicleService } from '../vehicle/vehicle.service';

/**
 * MainComponent implements this interface and binds itself to the handler in
 * ngAfterViewInit. The handler reads UI state (navigating, showing-map),
 * writes a few UI flags (loadingMap, failedPlan, navigationRecalculating),
 * and delegates a small number of operations that depend on ViewChildren
 * (wind panel, mobile panel routing, feedback modal) back to the host.
 *
 * The handler does NOT own the host's lifecycle — MainComponent's
 * ngOnDestroy must call `unbind()` to drop the reference.
 */
export interface CalculationFlowHost {
  readonly mapComponent?: MapComponent;
  readonly vehiclePanelComponent: VehiclePanelComponent;

  readonly isShowingMap: boolean;
  readonly isNavigating: WritableSignal<boolean>;
  readonly isNavigationRecalculating: WritableSignal<boolean>;
  readonly failedPlan: WritableSignal<boolean>;
  readonly loadingMap: WritableSignal<string | null>;

  openFeedbackDialog(): void;
  updateWindPanel(wind: WindElement[], temperature: number, weatherCode: number): void;
  setMobileVisibleRoutePanel(panel: string): void;
}

/**
 * Owns the UI side of the calculation flow. Subscribes to
 * `EvCalculationService.state$` and dispatches pending / success / error
 * effects against a bound host (MainComponent).
 *
 * Split from MainComponent because the side-effect logic for state
 * transitions is large (~400 lines) and self-contained: it doesn't need
 * MainComponent's voice-command, lifecycle, or settings code, and keeping
 * it in MainComponent obscured the calculation flow.
 *
 * Also owns:
 *   - the 10-minute better-route check timer (started/stopped by host
 *     around navigation lifecycle);
 *   - the "user accepted a faster alternative" subscription on
 *     ObservableBetterRoute (Replan state → onRouteSuccess);
 *   - the feedback-counter logic that surfaces a satisfaction prompt after
 *     30 successful calculations.
 */
@Injectable({ providedIn: 'root' })
export class EvCalculationFlowHandler {
  private host: CalculationFlowHost | null = null;
  private stateSub: Subscription | null = null;
  private betterRouteAcceptSub: Subscription | null = null;
  private betterRouteTimer: Subscription | null = null;
  private feedbackCounter: number = -1;

  private readonly evCalculationService = inject(EvCalculationService);
  private readonly waypointService = inject(WaypointService);
  private readonly mapService = inject(MapService);
  private readonly mapRangeDrawerService = inject(MapRangeDrawerService);
  private readonly mapRouteDrawerService = inject(MapRouteDrawerService);
  private readonly navigationMotionControllerService = inject(NavigationMotionControllerService);
  private readonly dataParserService = inject(DataParserService);
  private readonly webviewService = inject(WebviewService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly vehicleService = inject(VehicleService);
  private readonly injector = inject(Injector);

  constructor() {
    const stored = localStorage.getItem('feedbackCounter');
    this.feedbackCounter = stored != null ? parseInt(stored) : 0;
  }

  // NavigationService injects EvCalculationService injects this handler
  // (transitively through state$ subscription) — break cycle via Injector.
  private get navigationService(): NavigationService {
    return this.injector.get(NavigationService);
  }

  bind(host: CalculationFlowHost): void {
    this.host = host;
    if (!this.stateSub) {
      this.stateSub = this.evCalculationService.state$.subscribe(state => this.handle(state));
    }
    if (!this.betterRouteAcceptSub) {
      this.betterRouteAcceptSub = this.navigationService.ObservableBetterRoute.subscribe(resp => {
        if (resp && resp.state === BetterRouteState.Replan) {
          this.applyRouteSuccess(resp.routeData, resp.rangeParams);
        }
      });
    }
  }

  unbind(): void {
    this.stateSub?.unsubscribe();
    this.stateSub = null;
    this.betterRouteAcceptSub?.unsubscribe();
    this.betterRouteAcceptSub = null;
    this.stopBetterRouteTimer();
    this.host = null;
  }

  startBetterRouteTimer(): void {
    this.stopBetterRouteTimer();
    this.betterRouteTimer = timer(600000, 600000).subscribe(() => {
      if (this.navigationService.getChargeState() === ChargeState.None) {
        this.evCalculationService.requestBetterRouteCheck();
      }
    });
  }

  stopBetterRouteTimer(): void {
    if (this.betterRouteTimer) {
      this.navigationService.ObservableBetterRoute.next(null);
      this.betterRouteTimer.unsubscribe();
      this.betterRouteTimer = null;
    }
  }

  /**
   * Public so MainComponent's better-route-accept subscriber can call it
   * even before bind() has run (e.g. accept popup arrives during a setup
   * race). Idempotent w.r.t. host being null — early-returns.
   */
  applyRouteSuccess(resp: any, rangeParams: RangeParams): void {
    this.onRouteSuccess(resp, rangeParams);
  }

  // ============= state dispatch =============

  private handle(state: CalculationState): void {
    if (!this.host) return;
    if (state.status === 'idle') return;

    if (this.host.isShowingMap) {
      // Active showOnMap session — stop any range animation that pending
      // started, but otherwise ignore the calculation event.
      if (state.status === 'pending' && state.kind === 'range') {
        this.mapRangeDrawerService.stopLoadingAnimation();
      }
      return;
    }

    switch (state.status) {
      case 'pending':
        if (state.kind === 'range') this.onRangePending(state.rangeParams);
        else this.onRoutePending(state.rangeParams);
        return;
      case 'success':
        if (state.kind === 'range') this.onRangeSuccess(state.data, state.rangeParams);
        else this.onRouteSuccess(state.data, state.rangeParams);
        return;
      case 'error':
        this.handleCalculationError(state.error, state.rangeParams, state.kind);
        if (state.kind === 'route') this.routeCallEpilogue();
        return;
    }
  }

  // ============= pending =============

  private onRoutePending(rangeParams: RangeParams): void {
    if (!this.host) return;
    this.setLoadingMap('line');
    if (!this.host.isNavigating()) {
      this.evCalculationService.setFailedPlan({ failedPlan: false, maxReachedWaypoint: 0 });
      if (this.host.mapComponent) {
        this.host.mapComponent.drawRoutePlanningAnimationToMap(
          rangeParams.Waypoints.map(el => new LatLng(el.lat, el.lng)),
        );
      }
    }
  }

  private onRangePending(rangeParams: RangeParams): void {
    if (!this.host) return;
    this.mapRouteDrawerService.clearRoute();
    this.setLoadingMap('circle');
    if (this.host.mapComponent) {
      this.host.mapComponent.clearRange();
    }

    // Keep the camera off the cursor while the range is calculated and framed;
    // released a few seconds after the finished range is on screen.
    this.navigationMotionControllerService.beginRangeOverviewHold();

    const startWp = rangeParams.Waypoints[0];
    if (startWp) {
      const selectedCar = this.vehicleService.getSelectedCar();
      const soh = rangeParams.RangeInputParams.batteryStateOfHealth || 100;
      // Nominal remaining range. The drawer shortens it for the road-vs-circle
      // difference — don't pre-shrink it here as well.
      const estimatedRangeKm = selectedCar.FactoryRange * soh / 100 * rangeParams.RangeInputParams.batteryLevel / 100;
      this.mapRangeDrawerService.startLoadingAnimation([startWp.lng, startWp.lat], estimatedRangeKm);
    }
  }

  // ============= success =============

  private onRouteSuccess(resp: any, rangeParams: RangeParams): void {
    if (!this.host) return;
    if (this.host.isShowingMap) return;

    this.setLoadingMap(null);
    this.host.failedPlan.set(false);
    this.evCalculationService.setFailedPlan({ failedPlan: false, maxReachedWaypoint: 0 });
    this.waypointService.persistToStorage();
    const routeData: Route = this.dataParserService.parseRouteData(resp);

    if (this.waypointService.getSearchedRangeOrRoute() === false) {
      for (let i = 0; i < routeData.WaypointsAddresses.length; i++) {
        routeData.WaypointsAddresses[i].Location = rangeParams.Waypoints[i];
      }
      this.waypointService.setReverseGeocodedLocations(routeData.WaypointsAddresses);
    }

    this.mapService.setLastRouteData(routeData);

    if (!this.host.isNavigating()) {
      if (this.host.mapComponent) {
        this.host.mapComponent.drawRouteToMap(routeData);
      }
      this.tickFeedbackCounter();
    } else {
      this.host.isNavigationRecalculating.set(false);
      if (this.host.mapComponent) {
        this.host.mapComponent.setPlannedRoute(routeData);
      }
    }

    if (!this.host.isNavigating()) {
      if (this.host.mapComponent) {
        this.host.mapComponent.initWind(routeData.Wind);
      }
    }
    this.host.updateWindPanel(routeData.Wind, routeData.Temperature, routeData.WeatherCode);

    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.RouteUpdate(JSON.stringify(resp));
    }
    this.routeCallEpilogue();
  }

  private onRangeSuccess(resp: any, rangeParams: RangeParams): void {
    if (!this.host) return;
    // Only sync the server-returned SOC back if the battery level has not
    // changed since this request was sent; otherwise the stale response
    // would clobber the user's newer stepper/slider input.
    const sentBattery = rangeParams.RangeInputParams?.batteryLevel;
    const currentBattery = this.vehicleService.getECarSettings()?.batteryLevel;
    if (sentBattery === currentBattery) {
      this.vehicleService.setBatterySilent(resp.SOC);
      this.vehicleService.ObservableBatteryChanged.next(resp.SOC);
      this.host.vehiclePanelComponent.setbatteryLevel(resp.SOC);
    }
    const rangeData: Range = this.dataParserService.parseRangeData(resp);

    this.setLoadingMap(null);
    this.tickFeedbackCounter();
    this.waypointService.persistToStorage();
    if (this.host.mapComponent) {
      this.host.mapComponent.initWind(rangeData.Wind);
    }
    this.mapRangeDrawerService.drawRange(rangeData);
    this.navigationMotionControllerService.scheduleRangeOverviewRelease();
    this.host.updateWindPanel(rangeData.Wind, rangeData.Temperature, rangeData.WeatherCode);
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.RhaPolygonUpdate(JSON.stringify(resp));
    }
  }

  private routeCallEpilogue(): void {
    if (!this.host) return;
    this.navigationService.skipChargingStation = false;
    this.host.setMobileVisibleRoutePanel('routepoints');
  }

  // ============= error =============

  private handleCalculationError(err: CalculationError, rangeParams: RangeParams, ctx: 'route' | 'range'): void {
    if (!this.host) return;

    // No range to frame — hand the camera back, but leave the overview available
    // so a successful retry still gets it.
    if (ctx === 'range') this.navigationMotionControllerService.abortRangeOverviewHold(false);

    const setFailed = (maxReachedWaypoint: number) => {
      if (!this.host) return;
      this.host.failedPlan.set(true);
      this.evCalculationService.setFailedPlan({ failedPlan: true, maxReachedWaypoint });
      this.host.isNavigationRecalculating.set(false);
    };
    const trimRangeWaypoints = () => {
      if (this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE) {
        this.waypointService.setWaypointParams([this.waypointService.getWaypointsParams()[0]]);
      }
    };
    const drawFailedRoute = (maxReachedWaypoint: number) => {
      if (!this.host) return;
      if (this.host.mapComponent) {
        this.host.mapComponent.drawFailedRouteToMap(maxReachedWaypoint);
      }
    };
    const notifyWebviewRouteCleared = () => {
      if (this.webviewService.IsGPSTWebview()) this.webviewService.RouteUpdate('');
    };
    const notifyWebviewRangeCleared = () => {
      if (this.webviewService.IsGPSTWebview()) this.webviewService.RhaPolygonUpdate('');
    };
    const iconUrl = (name: string) => `/assets/icons/${this.settingsService.getMap().Skin}/error/${name}`;
    const lang = this.languageService.languageJSON;

    switch (err.kind) {
      case 'route_limit_exceeded': {
        this.openErrorDialog(lang.App_ErrorDialog_RouteLimit, iconUrl('popup_icon_toofar.svg'));
        setFailed(0);
        this.setLoadingMap(null);
        trimRangeWaypoints();
        drawFailedRoute(0);
        return;
      }
      case 'free_tier_limit': {
        this.stackedModalsService.openModal(SubscriptionRequiredDialogComponent, { data: { feature: 'route-planning' }, autoFocus: false });
        setFailed(0);
        this.setLoadingMap(null);
        trimRangeWaypoints();
        drawFailedRoute(0);
        return;
      }
      case 'country_not_supported': {
        if (ctx === 'route') {
          this.setLoadingMap(null);
          setFailed(0);
          this.openErrorDialog(lang.App_ErrorDialog_CountryNotSupported, iconUrl('popup_icon_nomap.svg'));
          trimRangeWaypoints();
          drawFailedRoute(0);
          notifyWebviewRouteCleared();
        } else {
          this.mapRangeDrawerService.stopLoadingAnimation();
          this.setLoadingMap(null);
          this.openErrorDialog(lang.App_ErrorDialog_CountryNotSupported, iconUrl('popup_icon_nomap.svg'));
          notifyWebviewRangeCleared();
        }
        return;
      }
      case 'closest_route_too_far': {
        this.mapRangeDrawerService.stopLoadingAnimation();
        this.setLoadingMap(null);
        this.openErrorDialog(lang.App_ErrorDialog_ClosestRouteTooFar, iconUrl('popup_icon_nomap.svg'));
        if (this.host.mapComponent) {
          this.host.mapComponent.clearMapLayers();
        }
        notifyWebviewRangeCleared();
        return;
      }
      case 'cannot_calculate_route': {
        if (err.source === 'server' && this.host.isNavigating()) {
          if (this.navigationService.skipChargingStation) {
            this.openSkipChargingStationFailedDialog();
            localStorage.setItem('skippedChargingStation', JSON.stringify(null));
            this.navigationService.ObservableCannotSkipMoreChargingStation.next(true);
          } else {
            this.navigationService.ObservableChangeNavigation.next('exit');
          }
        } else {
          this.setLoadingMap(null);
          setFailed(err.maxReachedWaypoint);
          this.openErrorDialog(lang.App_ErrorDialog_CannotCalculateRoute, iconUrl('popup_icon_nomap.svg'));

          if (this.waypointService.getSearchedRangeOrRoute() === false && err.waypointsInfo) {
            for (let i = 0; i < err.waypointsInfo.length; i++) {
              (err.waypointsInfo[i] as WaypointAddress).Location = rangeParams.Waypoints[i];
            }
            if (!(err.source === 'server' && err.waypointsInfo.length === 0)) {
              this.waypointService.setReverseGeocodedLocations(err.waypointsInfo);
            }
          }
          trimRangeWaypoints();
          drawFailedRoute(err.maxReachedWaypoint);
        }
        notifyWebviewRouteCleared();
        return;
      }
      case 'cannot_calculate_range': {
        this.mapRangeDrawerService.stopLoadingAnimation();
        this.setLoadingMap(null);
        this.openErrorDialog(lang.App_ErrorDialog_CannotCalculateRange, iconUrl('popup_icon_nomap.svg'));
        if (err.source === 'invalid_response' && this.host.mapComponent) {
          this.host.mapComponent.clearMapLayers();
        }
        notifyWebviewRangeCleared();
        return;
      }
      case 'route_network': {
        // Nav-mode retry handled inside EvCalculationService via retry({ delay }).
        // This branch fires only outside navigation.
        this.setLoadingMap(null);
        setFailed(0);
        this.openErrorDialog(lang.App_ErrorDialog_CannotCalculateRoute, iconUrl('popup_icon_nomap.svg'));
        drawFailedRoute(0);
        notifyWebviewRouteCleared();
        return;
      }
      case 'no_charger_types': {
        this.openErrorDialog(lang.App_ErrorDialog_SelectChargingStation, iconUrl('popup_icon_select_charging_station.svg'));
        if (ctx === 'range') {
          this.mapRangeDrawerService.stopLoadingAnimation();
          this.setLoadingMap(null);
        }
        return;
      }
    }
  }

  // ============= helpers =============

  private setLoadingMap(loading: string | null): void {
    if (!this.host) return;
    if (this.host.loadingMap() === 'line' && loading !== 'line' && !this.host.isNavigating() && this.host.mapComponent) {
      this.host.mapComponent.clearMapLayers();
    }
    this.host.loadingMap.set(loading);
  }

  private openErrorDialog(title: string, icon: string, message: string | null = null): void {
    if (!this.host) return;
    if ((!this.host.isNavigating() || this.host.isNavigationRecalculating()) &&
      document.getElementsByClassName('mat-dialog-title').length === 0) {
      const selectedDisplayMode = this.waypointService.getSelectedDisplayMode();
      const batteryLevel = this.vehicleService.getECarSettings()?.batteryLevel ?? 100;
      const war = selectedDisplayMode === DisplayMode.ROUTE && batteryLevel < 10
        ? this.languageService.languageJSON.App_WarningLowBattery : null;
      this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
        data: { title, icon, message, warning: war, buttons: [this.languageService.languageJSON.Global_Ok, null] },
        autoFocus: false,
      });

      if (this.host.isNavigating()) {
        this.navigationService.ObservableChangeNavigation.next('exit');
      }
    }
  }

  private openSkipChargingStationFailedDialog(): void {
    this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.Global_Alert,
        message: this.languageService.languageJSON.App_CannotSkipChargingation,
        icon: `/assets/icons/${this.settingsService.getMap().Skin}/error/skip_chargingstation_image.svg`,
        buttons: [this.languageService.languageJSON.Global_Ok, null],
      },
      autoFocus: false,
    });
  }

  private tickFeedbackCounter(): void {
    if (!this.host) return;
    if (this.feedbackCounter === -1 || this.feedbackCounter >= 30) return;
    this.feedbackCounter++;
    localStorage.setItem('feedbackCounter', this.feedbackCounter.toString());
    if (this.feedbackCounter !== 30) return;

    timer(10000).pipe(first()).subscribe(() => {
      if (!this.host) return;
      if (this.host.isNavigating()) {
        // Defer past navigation — don't bother the user during driving.
        this.feedbackCounter--;
      } else {
        this.host?.openFeedbackDialog();
      }
    });
  }
}
